import { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { PageHeader } from '../features/shared/PageHeader';
import { InlineBanner } from '../features/shared/InlineBanner';
import { SummaryMetrics } from '../features/usage/SummaryMetrics';
import { UsageFilters } from '../features/usage/UsageFilters';
import { UsageFeed, recordMatchesFilter } from '../features/usage/UsageFeed';
import { LogContentToggle } from '../features/usage/LogContentToggle';
import { ClientQuotaAlertBanner } from '../features/usage/ClientQuotaAlertBanner';
import { useGqlToken, useMutation, useQuery } from '../gql/hooks';
import { graphqlRequest } from '../gql/client';
import { useSubscription } from '../gql/useSubscription';
import {
  SET_LOG_CONTENT_MUTATION,
  SYSTEM_SETTINGS_QUERY,
  USAGE_RECORDED_SUBSCRIPTION,
  USAGE_RECORDS_QUERY,
  USAGE_SUMMARY_QUERY,
  type SetLogContentResult,
  type SetLogContentVariables,
  type SystemSettingsQueryResult,
  type UsageRecordedSubscriptionResult,
  type UsageRecordsQueryResult,
  type UsageRecordsQueryVariables,
  type UsageSummaryQueryResult,
  type UsageSummaryQueryVariables,
} from '../gql/operations';
import type { UsageFilter, UsageRecord } from '../gql/types';

// Live usage dashboard (US-8.5.1).
//
//   • Headline metrics from `usageSummary` (owned-infra share, per-provider failure rates, token
//     volume + byProvider share viz).
//   • A filterable, cursor-paginated `usageRecords` feed. Filters drive BOTH the query variables
//     and the live subscription's filter, so the live stream matches the visible window.
//   • The `usageRecorded` subscription PREPENDS new records live (re-checked against the active
//     filter client-side), with a pause/resume control. When paused, the subscription is disabled
//     entirely (no socket churn beyond what's needed).
//
// Records live in local state because they come from two sources (the paginated query + the live
// stream). The query result seeds/extends the list; live pushes prepend onto it.

const PAGE_SIZE = 25;

// Hard cap on the in-memory feed. Live pushes prepend without bound otherwise, so a busy session
// would grow `records` (and the `liveIds` Set, reconciled per render) indefinitely. We keep the
// most recent rows and drop the tail; the operator can re-query for older windows via the filter.
const MAX_LIVE_ROWS = 500;

/** Serialize a filter so effects depend on its value, not its identity. */
function filterKey(filter: UsageFilter): string {
  return JSON.stringify(filter);
}

export function UsageDashboardPage() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const token = useGqlToken();

  const [filter, setFilter] = useState<UsageFilter>({});
  const [paused, setPaused] = useState(false);

  // Feed state: the accumulated records + cursor/pagination + ids that arrived live.
  const [records, setRecords] = useState<UsageRecord[]>([]);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [liveIds, setLiveIds] = useState<Set<string>>(() => new Set());

  const fkey = filterKey(filter);

  // Headline summary (re-runs when the filter changes).
  const summary = useQuery<UsageSummaryQueryResult, UsageSummaryQueryVariables>(
    USAGE_SUMMARY_QUERY,
    { filter },
  );

  // Runtime-toggleable settings (2026-06-08). Drives the "Log prompt + reply
  // content" switch shown right under the page header.
  const settings = useQuery<SystemSettingsQueryResult>(SYSTEM_SETTINGS_QUERY);
  const setLogContent = useMutation<SetLogContentResult, SetLogContentVariables>(
    SET_LOG_CONTENT_MUTATION,
  );

  // First page of the feed (re-runs when the filter changes). On success we seed local state.
  const feedPage = useQuery<UsageRecordsQueryResult, UsageRecordsQueryVariables>(
    USAGE_RECORDS_QUERY,
    { filter, first: PAGE_SIZE },
  );

  // Seed local feed state from the first-page query whenever it resolves (per filter).
  useEffect(() => {
    const conn = feedPage.data?.usageRecords;
    if (!conn) return;
    setRecords(conn.edges.map((e) => e.node));
    setEndCursor(conn.pageInfo.endCursor ?? null);
    setHasNextPage(conn.pageInfo.hasNextPage);
    setLiveIds(new Set());
    // Depend on the resolved data + filter key; feedPage.data identity changes on each fetch.
  }, [feedPage.data, fkey]);

  // Load the next cursor page and append.
  const loadMore = useCallback(async () => {
    if (!endCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await graphqlRequest<UsageRecordsQueryResult, UsageRecordsQueryVariables>(
        USAGE_RECORDS_QUERY,
        { filter, first: PAGE_SIZE, after: endCursor },
        { token },
      );
      const conn = data.usageRecords;
      setRecords((prev) => {
        const seen = new Set(prev.map((r) => r.id));
        const next = conn.edges.map((e) => e.node).filter((n) => !seen.has(n.id));
        return [...prev, ...next];
      });
      setEndCursor(conn.pageInfo.endCursor ?? null);
      setHasNextPage(conn.pageInfo.hasNextPage);
    } catch {
      // Surfaced via the page-level error banner path if the first page also failed; load-more
      // failures are non-fatal — the operator can retry.
    } finally {
      setLoadingMore(false);
    }
  }, [endCursor, loadingMore, filter, token]);

  // Live append: prepend pushed records that match the active filter. Disabled while paused.
  const handleLive = useCallback(
    (data: UsageRecordedSubscriptionResult) => {
      const record = data.usageRecorded;
      if (!record) return;
      if (!recordMatchesFilter(record, filter)) return;
      let survivingIds: Set<string> | null = null;
      setRecords((prev) => {
        if (prev.some((r) => r.id === record.id)) return prev;
        const next = [record, ...prev];
        // Cap the in-memory feed so live prepends can't grow it without bound.
        if (next.length > MAX_LIVE_ROWS) {
          const capped = next.slice(0, MAX_LIVE_ROWS);
          survivingIds = new Set(capped.map((r) => r.id));
          return capped;
        }
        return next;
      });
      setLiveIds((prev) => {
        const next = new Set(prev);
        next.add(record.id);
        // Prune live ids that no longer correspond to a row in the (capped) feed.
        if (survivingIds) {
          for (const id of next) {
            if (!survivingIds.has(id)) next.delete(id);
          }
        }
        return next;
      });
    },
    [filter],
  );

  const { status } = useSubscription<UsageRecordedSubscriptionResult>(
    USAGE_RECORDED_SUBSCRIPTION,
    {
      onData: handleLive,
      variables: { filter },
      enabled: !paused,
    },
  );

  // Track whether the socket auth-closed so we can show a degraded banner (not a crash).
  const wasOpen = useRef(false);
  useEffect(() => {
    if (status === 'open') wasOpen.current = true;
  }, [status]);
  const degraded = !paused && wasOpen.current && (status === 'closed' || status === 'errored');

  const summaryError = summary.error;
  const feedError = feedPage.error;

  return (
    <div>
      <PageHeader
        title="Usage"
        description="Live request telemetry and per-provider rollups. Set whether prompt and reply content is captured per request below."
        breadcrumbs={[{ label: 'Usage' }]}
      />

      {/* Clients over their monthly quota this month (2026-08-09) — the alert surface for
          warn-mode quotas, which never block traffic on their own. */}
      <ClientQuotaAlertBanner />

      <LogContentToggle
        value={settings.data?.systemSettings.logContent ?? false}
        loading={settings.loading}
        saving={setLogContent.loading}
        error={setLogContent.error?.message ?? null}
        onChange={async (next) => {
          try {
            await setLogContent.mutate({ enabled: next });
            settings.refetch();
          } catch {
            // Surfaced inline via setLogContent.error.
          }
        }}
      />

      {summaryError && (
        <InlineBanner tone="error">
          Couldn’t load the usage summary: {summaryError.message}
        </InlineBanner>
      )}

      <SummaryMetrics summary={summary.data?.usageSummary ?? null} loading={summary.loading} />

      <div className="mt-8">
        <UsageFilters value={filter} onChange={setFilter} />

        {degraded && (
          <InlineBanner tone="warning" testId="live-degraded">
            Live stream disconnected (the session may have expired). Showing the last loaded
            records — resume or reload to reconnect.
          </InlineBanner>
        )}

        {feedError && (
          <InlineBanner tone="error">
            Couldn’t load the usage feed: {feedError.message}
          </InlineBanner>
        )}

        <UsageFeed
          records={records}
          liveIds={liveIds}
          loading={feedPage.loading}
          hasNextPage={hasNextPage}
          loadingMore={loadingMore}
          onLoadMore={() => void loadMore()}
          paused={paused}
          onTogglePaused={() => setPaused((p) => !p)}
          live={status === 'open'}
        />
      </div>

      <p className={`mt-4 text-xs ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
        Showing {records.length} record{records.length === 1 ? '' : 's'}
        {summary.data?.usageSummary
          ? ` · ${summary.data.usageSummary.totalRequests.toLocaleString()} total in window`
          : ''}
      </p>
    </div>
  );
}
